import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  UseQueryResult,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Helper function to replace path params like :projectId, :kernelId, etc.
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key)
  );
}

export interface ProgressDetailsResponse {
  isSuccess: boolean;
  message: string;
  data: {
    submission: {
      id: string;
      status: string;
      submittedAt: string;
      timeSpent: number;
      attempts: number;
    };
    student: {
      id: string;
      name: string;
      email: string;
    };
    project: {
      id: string;
      title: string;
      description: string;
      dueDate: string;
      course: {
        id: string;
        name: string;
        code: string;
      };
    };
    progressSummary: {
      totalCheckpoints: number;
      completedCheckpoints: number;
      progressPercentage: number;
      totalRubricPoints: number;
    };
    checkpoints: Checkpoint[];
    gradeDetails: {
      id: string;
      totalScore: string;
      maxScore: string;
      percentage: string;
      letterGrade: string;
      overallFeedback: string;
      gradedAt: string;
    };
  };
}

export interface Checkpoint {
  id: string;
  title: string;
  description: string;
  checkpointNumber: number;
  dueDate: string;
  weightPercentage: string;
  isRequired: boolean;
  status: string;
  isCompleted: boolean;
  checkpointFeedback: string;
  progress: {
    id: string;
    status: string;
    startedAt: string | null;
    submittedAt: string | null;
    completedAt: string | null;
    timeSpentMinutes: number;
    lastActivity: string | null;
  };
  rubrics: Rubric[];
}

export interface Rubric {
  id: string;
  title: string;
  description: string;
  criteria: RubricCriteria[];
  totalPoints: number;
  isTemplate: boolean;
}

export interface RubricCriteria {
  name: string;
  description: string;
  points: number;
  currentScore?: number; // optional because not all criteria have it
}

export interface GradeResponse {
  isSuccess: boolean;
  message: string;
  data: GradeData;
}

export interface GradeData {
  id: string;
  totalScore: string; // comes as string in your payload
  maxScore: string; // string too
  percentage: string;
  letterGrade: string;
  feedback: string;
  checkpointBreakdown: Record<string, CheckpointBreakdown>;
  gradedAt: string; // ISO date string
}

export interface CheckpointBreakdown {
  checkpointTitle: string;
  rubrics: Rubric[];
  checkpointTotalScore: number;
  checkpointMaxScore: number;
}

export interface Rubric {
  rubricId: string;
  rubricTitle: string;
  criteriaScores: CriteriaScore[];
  totalScore: number;
  maxScore: number;
}

export interface CriteriaScore {
  criterionName: string;
  score: number;
  maxPoints: number;
}

export interface GradeSubmissionRequest {
  submissionId: string;
  checkpointRubrics: CheckpointRubric[];
  overallFeedback: string;
  totalPoints: number;
  maxPoints: number;
}

export interface CheckpointRubric {
  checkpointId: string;
  totalPoints: number;
  maxPoints: number;
  rubrics: RubricScore[];
}

export interface RubricScore {
  rubricId: string;
  criteriaScores: CriteriaScore[];
}

export interface CriteriaScore {
  criterionName: string;
  score: number;
  maxPoints: number;
}

export async function getGrades(
  submissionId: string
): Promise<ProgressDetailsResponse> {
  const url = getBITSEndApiMap("getGrades").replace(
    ":submissionId",
    submissionId
  );
  const { data } = await api.get<ProgressDetailsResponse>(url);
  return data;
}

//Save Rubrics
export async function saveRubrics(
  gradeSubmissionRequest: GradeSubmissionRequest
): Promise<GradeResponse> {
  const url = getBITSEndApiMap("createGrades");
  const { data } = await api.post<GradeResponse>(url, gradeSubmissionRequest);
  return data;
}

export function useSaveRubricsMutation() {
  return useMutation({
    mutationFn: saveRubrics,
  });
}
